import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { SERVICES } from "../lib/site";
import SectionHeader from "../components/SectionHeader";
import Seo from "../components/Seo";

export default function Services() {
    const { hash } = useLocation();

    useEffect(() => {
        if (hash) {
            const el = document.getElementById(hash.replace("#", ""));
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, [hash]);

    return (
        <div data-testid="services-page">
            <Seo
                title="Services — Extensions, Lofts, Groundworks, Electrical & Mechanical"
                description="Everything we do — from single-storey extensions and loft conversions to full rewires, boiler installs and period restorations across Berkshire."
                path="/services"
            />
            {/* Page hero */}
            <section className="bg-muted border-b border-border">
                <div className="container-x py-20 md:py-28">
                    <SectionHeader
                        eyebrow="Our Services"
                        title="One trusted team for every part of your build."
                        lead="From foundations to finish, electrical to extensions — we self-deliver every specialism so you have one point of contact, one team on site, and one fixed quote."
                    />
                </div>
            </section>

            {/* Service blocks */}
            <section className="section-pad container-x space-y-24">
                {SERVICES.map((s, i) => {
                    const Icon = s.icon;
                    const reverse = i % 2 === 1;
                    return (
                        <article
                            id={s.slug}
                            key={s.slug}
                            className="grid md:grid-cols-12 gap-10 md:gap-16 items-center scroll-mt-28"
                            data-testid={`service-${s.slug}`}
                        >
                            <div className={`md:col-span-5 ${reverse ? "md:order-2" : ""}`}>
                                <div className="aspect-[4/3] rounded-lg bg-primary/5 border border-border p-12 grid place-items-center">
                                    <Icon
                                        size={120}
                                        strokeWidth={0.8}
                                        className="text-primary opacity-90"
                                    />
                                </div>
                            </div>
                            <div className={`md:col-span-7 ${reverse ? "md:order-1" : ""}`}>
                                <div className="eyebrow">0{i + 1} · Service</div>
                                <h2 className="mt-3 text-3xl md:text-4xl font-extrabold tracking-tight font-heading">
                                    {s.name}
                                </h2>
                                <p className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed">
                                    {s.description}
                                </p>
                                <ul className="mt-7 grid sm:grid-cols-2 gap-3">
                                    {s.bullets.map((b) => (
                                        <li
                                            key={b}
                                            className="flex items-start gap-3 text-sm font-medium"
                                        >
                                            <span className="mt-0.5 h-5 w-5 rounded-full bg-primary text-primary-foreground grid place-items-center shrink-0">
                                                <Check size={12} strokeWidth={3} />
                                            </span>
                                            {b}
                                        </li>
                                    ))}
                                </ul>
                                <Link
                                    to="/quote"
                                    className="btn-primary mt-8"
                                    data-testid={`service-quote-${s.slug}`}
                                >
                                    Quote for {s.name} <ArrowRight size={18} />
                                </Link>
                            </div>
                        </article>
                    );
                })}
            </section>
        </div>
    );
}
